import { ARCInfo } from "arcjs/common/arc_devices";
import { AnyPlainObject } from "arcjs/common/misc";
import {
    DUMMY_FLEETS_INFO,
    ROBOTS_TO_CHANGE,
    ROBOT_CHANGING_ARC,
    genDummyStatus,
} from "lib/consts/testing";
import { getHTTPAddress, getWSAddress } from "lib/utils/connection";

import uniqBy from "lodash/uniqBy";
import { Fleet } from "types/fleet";
import { Socket, io } from "socket.io-client";
import { MapInfo } from "types/fleet";
import { MapMetadata } from "arcjs/common/navigation";
import { Values } from "types/utilities";

const FLEET_IS_SECURE = process.env.REACT_APP_FLEET_HOST_SECURE === "true";
const FLEET_HOST = process.env.REACT_APP_FLEET_HOST;
const FLEET_PORT = process.env.REACT_APP_FLEET_PORT;
const FLEETMAPS_HOST = process.env.REACT_APP_FLEETMAPS_HOST;
const FLEETMAPS_PORT = process.env.REACT_APP_FLEETMAPS_PORT;
const FLEETMAPS_IS_SECURE = JSON.parse(process.env.REACT_APP_FLEETMAPS_HOST_SECURE ?? "false");

const FLEET_URL = getHTTPAddress({ isSecure: FLEET_IS_SECURE, host: FLEET_HOST, port: FLEET_PORT });
const FLEETMAPS_URL = getHTTPAddress({
    isSecure: FLEETMAPS_IS_SECURE,
    host: FLEETMAPS_HOST,
    port: FLEETMAPS_PORT,
});
export const FLEET_STATUS_URL = FLEET_URL;

const HTTPMethods = {
    GET: "GET",
    PUT: "PUT",
    POST: "POST",
    DELETE: "DELETE",
} as const;

class NotFoundError extends Error { }

const StatusCodeErrorMap: Record<number, new (message?: string) => Error> = {
    404: NotFoundError,
    500: Error,
};

const getErrorMessage = async (res: Response) => {
    try {
        const { detail = "None" } = await res.json();
        return detail;
    } catch {
        return "None";
    }
};

const fetchWithBaseUrl =
    (baseURL: string) =>
        async <ResponseType>(
            method: Values<typeof HTTPMethods>,
            endpoint: string,
            body: AnyPlainObject | null = null
        ) => {
            const url = `${baseURL}/${endpoint}`;
            const res = await fetch(url, {
                method,
                body: JSON.stringify(body),
                headers: {
                    "Content-Type": "application/json",
                },
            });
            if (!res.ok) {
                const ErrorClass = StatusCodeErrorMap[res.status];
                const errorMessage = await getErrorMessage(res);
                if (!ErrorClass)
                    throw new Error(`Error occurred while fetching ${url}. Message: ${errorMessage}`);
                else throw new ErrorClass(errorMessage);
            } else {
                return (await res.json()) as ResponseType;
            }
        };

class DummySource {
    fleets: typeof DUMMY_FLEETS_INFO;
    robotsPool: typeof ROBOTS_TO_CHANGE;
    robotPoolIndex: number;
    constructor() {
        this.fleets = DUMMY_FLEETS_INFO;
        this.robotsPool = ROBOTS_TO_CHANGE;
        this.robotPoolIndex = 0;
    }

    mockRobotChange(onChange: FleetChangesSubscriber) {
        for (const arc of this.fleets.flatMap((f) => f.arcs)) {
            if (arc.serialNumber === ROBOT_CHANGING_ARC) {
                arc.robot = ROBOTS_TO_CHANGE[this.robotPoolIndex];
                onChange(arc);
                break;
            }
        }
        this.robotPoolIndex = (this.robotPoolIndex + 1) % this.robotsPool.length;
    }
}

class DummyFeedHandle {
    intervalId: ReturnType<typeof setInterval>;
    constructor(intervalId: ReturnType<typeof setInterval>) {
        this.intervalId = intervalId;
    }

    close() {
        clearInterval(this.intervalId);
    }
}

class ARCChangeFeedHandle {
    socket: Socket;
    constructor(socket: Socket) {
        this.socket = socket;
    }

    close() {
        this.socket.close();
    }
}

export type FleetChangesSubscriber = {
    (arc: ARCInfo): void;
};

export class FleetRepository {
    useDummy: boolean;
    dummySource: DummySource | null;
    fleetStatusURl: string;
    fleetUrl: string;
    fleetmapsUrl: string;
    fleetFetch: ReturnType<typeof fetchWithBaseUrl>;
    statusFetch: ReturnType<typeof fetchWithBaseUrl>;
    fleetmapsFetch: ReturnType<typeof fetchWithBaseUrl>;

    constructor({
        fleetStatusUrl = FLEET_STATUS_URL,
        fleetUrl = FLEET_URL,
        fleetmapsUrl = FLEETMAPS_URL,
        useDummy = false,
    } = {}) {
        this.useDummy = useDummy;
        this.dummySource = this.useDummy ? new DummySource() : null;
        this.fleetStatusURl = fleetStatusUrl;
        this.fleetUrl = fleetUrl;
        this.fleetmapsUrl = fleetmapsUrl;
        this.fleetFetch = fetchWithBaseUrl(this.fleetUrl);
        this.statusFetch = fetchWithBaseUrl(this.fleetStatusURl);
        this.fleetmapsFetch = fetchWithBaseUrl(this.fleetmapsUrl);
    }

    async updateOne(fleet: Fleet) {
        if (this.useDummy) return;
        const res = await this.fleetFetch(HTTPMethods.POST, "update_fleet", fleet);
        return res;
    }

    async getAll(): Promise<Fleet[]> {
        if (this.useDummy && this.dummySource) return this.dummySource.fleets;
        const fleets = await this.fleetFetch<Fleet[]>(HTTPMethods.POST, "get_all_fleets", {});
        return fleets;
    }

    async getById(fleetId: string) {
        if (this.useDummy && this.dummySource) return this.dummySource.fleets.find((f) => f.id === fleetId);

        const fleet = await this.fleetFetch(HTTPMethods.POST, "get_fleet", { fleetId });
        return fleet;
    }

    async getARCBySerial(serialNumber: string) {
        if (this.useDummy && this.dummySource)
            return this.dummySource.fleets
                .flatMap((f) => f.arcs)
                .find((arc) => arc.serialNumber === serialNumber);

        const arc = await this.fleetFetch(HTTPMethods.POST, "get_arc", { serialNumber });
        return arc;
    }

    async getARCStatus(serialNumber: string) {
        if (this.useDummy) return genDummyStatus();

        const arcStatus = await this.statusFetch(HTTPMethods.POST, "get_arc_status", {
            serialNumber,
        });
        return arcStatus;
    }

    async getAllArcs() {
        const fleets = await this.getAll();
        return uniqBy(
            fleets.flatMap((f) => f.arcs),
            (a) => a.serialNumber
        );
    }

    // TODO: implement fetching data from APIs from fleet maps, which hosts map data and mission data
    // for docs see fleetmaps.demo-fleet.o.r2c2.ai/docs
    async getAllMaps() {
        const maps = await this.fleetmapsFetch<MapInfo[]>(HTTPMethods.GET, "/maps");
        return maps;
    }

    async getMap(mapId: string) {
        const map = await this.fleetmapsFetch<MapInfo>(HTTPMethods.GET, `/maps/${mapId}`);
        return map;
    }

    async getMapMetadata(mapId: string) {
        const metadata = await this.fleetmapsFetch<MapMetadata>(
            HTTPMethods.GET,
            `/maps/${mapId}/metadata`
        );
        return metadata;
    }

    async assignMission(serialNumber: string, mission_id: string, fleet_id: string | null = null) {
        const body: AnyPlainObject = { serialNumber, mission_id };
        if (fleet_id) body.fleet_id = fleet_id;
        await this.fleetFetch(HTTPMethods.POST, `assign_mission`, body);
    }

    async getAssignedMission(serialNumber: string) {
        return await this.fleetFetch(HTTPMethods.POST, `get_assigned_mission`, { serialNumber });
    }

    async subscribeChanges(onChange: FleetChangesSubscriber) {
        if (this.useDummy) {
            const intervalId = setInterval(() => {
                console.log("mocking change");
                this.dummySource?.mockRobotChange((arc) => {
                    // make the arc object is a new one
                    onChange({ ...arc });
                });
            }, 5000);
            return new DummyFeedHandle(intervalId);
        }

        const socket = io(getWSAddress({ host: FLEET_HOST, subpath: "/get_arc_changes" }));

        socket.on("change", (arc) => {
            // arc sent over the network is always a new copy
            onChange(arc);
        });

        return new ARCChangeFeedHandle(socket);
    }
}
